/**
 * Partners: the people who send us work.
 *
 * A realtor whose buyer just got a roof line written into an inspection
 * report, an adjuster who wants a second opinion before an argument, a property
 * manager with forty pipe boots. None of them are customers. All of them are
 * one phone call away from being the reason a job exists.
 *
 * Everything in here is about one thing: a text has to arrive looking like a
 * text from a person. Not a campaign, not a broadcast, not something with a
 * footer. That constraint is what decides the shape of this module —
 *
 *   * the message goes out from the **rep's own iPhone**, so this package
 *     builds an `sms:` link and never sends anything (see `smsLink`);
 *   * consent is a **spoken yes**, recorded, because there is no webhook on a
 *     personal phone to catch a STOP;
 *   * the send window and the pacing hint are **warnings, not gates**, because
 *     the person tapping Send is the sender and this application is not.
 *
 * See ADR 0067.
 */

/* ------------------------------------------------------------ categories -- */

/**
 * What kind of partner this is.
 *
 * The category is not decoration: it decides which monthly templates a partner
 * is eligible for, and whether a storm text reaches them for a county they do
 * not live in. Adjusters and insurance agents work claims wherever the claims
 * are, so they hear about every storm; a realtor hears about their own county.
 */
export const PARTNER_CATEGORIES = [
  "realtor",
  "insurance_agent",
  "adjuster",
  "property_manager",
  "hoa",
  "mortgage_lender",
  "home_inspector",
  "contractor_trade",
  "networking",
  "other",
] as const;

export type PartnerCategory = (typeof PARTNER_CATEGORIES)[number];

/** What the category is called on screen. */
export const PARTNER_CATEGORY_LABELS: Record<PartnerCategory, string> = {
  realtor: "Realtor",
  insurance_agent: "Insurance agent",
  adjuster: "Adjuster",
  property_manager: "Property manager",
  hoa: "HOA board",
  mortgage_lender: "Mortgage lender",
  home_inspector: "Home inspector",
  contractor_trade: "Trade / contractor",
  networking: "Networking contact",
  other: "Other",
};

/**
 * What the category is called *inside a sentence*.
 *
 * `{{category_noun}}` lands in "I've got a couple clients this month who need
 * a ___". That reads as a person talking only if the noun is the one a person
 * would say — "a realtor", not "a Realtor category partner". The two that have
 * no natural noun fall back to something honest rather than something
 * grammatical: nobody says "I've got clients who need a networking contact".
 */
export const PARTNER_CATEGORY_NOUNS: Record<PartnerCategory, string> = {
  realtor: "realtor",
  insurance_agent: "insurance agent",
  adjuster: "adjuster",
  property_manager: "property manager",
  hoa: "board member",
  mortgage_lender: "lender",
  home_inspector: "home inspector",
  contractor_trade: "contractor",
  networking: "good contact",
  other: "good contact",
};

export function partnerCategoryLabel(
  category: string | null | undefined,
): string {
  return PARTNER_CATEGORY_LABELS[category as PartnerCategory] ?? "Other";
}

export function partnerCategoryNoun(
  category: string | null | undefined,
): string {
  return PARTNER_CATEGORY_NOUNS[category as PartnerCategory] ?? "good contact";
}

/**
 * True when this partner hears about a storm in a county that is not theirs.
 *
 * Claims work follows the hail, not the office. An adjuster in Hoover is
 * assigned to Jefferson County the week Jefferson County gets hit, and the
 * county report is the thing they will actually forward — so they get every
 * county's text. Everybody else gets their own.
 */
export function hearsAboutEveryCounty(
  category: string | null | undefined,
): boolean {
  return category === "adjuster" || category === "insurance_agent";
}

/* ---------------------------------------------------------------- county -- */

export const PARTNER_COUNTIES = ["shelby", "jefferson", "other"] as const;
export type PartnerCounty = (typeof PARTNER_COUNTIES)[number];

export const COUNTY_LABELS: Record<PartnerCounty, string> = {
  shelby: "Shelby County",
  jefferson: "Jefferson County",
  other: "Elsewhere",
};

export function countyLabel(county: string | null | undefined): string {
  return COUNTY_LABELS[county as PartnerCounty] ?? "Elsewhere";
}

/**
 * Jefferson County ZIPs, near enough.
 *
 * "Near enough" is the honest description and it is deliberate. ZIP codes are
 * postal delivery routes and counties are political boundaries; they were never
 * drawn to agree, and several of the ones that matter most here straddle the
 * line. 35244 (Hoover, Riverchase) is in both. 35242 (Inverness, Greystone) is
 * in both. So this is a guess that is right most of the time, it is made once
 * when a card is scanned, and **the rep can change it on the review screen** —
 * which is the part that makes the guess safe. A wrong county costs a partner
 * one irrelevant storm text, and the fix is a dropdown.
 *
 * Split ZIPs are assigned to the county holding the larger share of addresses.
 */
const JEFFERSON_ZIPS = new Set([
  "35005",
  "35006",
  "35020",
  "35021",
  "35022",
  "35023",
  "35060",
  "35061",
  "35064",
  "35068",
  "35071",
  "35073",
  "35091",
  "35094",
  "35111",
  "35116",
  "35117",
  "35118",
  "35126",
  "35127",
  "35130",
  "35172",
  "35173",
  "35180",
  "35201",
  "35202",
  "35203",
  "35204",
  "35205",
  "35206",
  "35207",
  "35208",
  "35209",
  "35210",
  "35211",
  "35212",
  "35213",
  "35214",
  "35215",
  "35216",
  "35217",
  "35218",
  "35219",
  "35220",
  "35221",
  "35222",
  "35223",
  "35224",
  "35226",
  "35228",
  "35229",
  "35231",
  "35232",
  "35233",
  "35234",
  "35235",
  "35236",
  "35237",
  "35238",
  "35243",
  "35246",
  "35249",
  "35253",
  "35254",
  "35255",
  "35259",
  "35260",
  "35261",
  "35266",
  "35282",
  "35283",
  "35285",
  "35287",
  "35288",
  "35290",
  "35291",
  "35292",
  "35293",
  "35294",
  "35295",
  "35296",
  "35297",
  "35298",
]);

/**
 * Shelby County ZIPs.
 *
 * 35242 and 35244 are here rather than in Jefferson because the majority of
 * addresses in both sit south of the county line, which is where the houses
 * with roofs worth replacing mostly are. Same caveat as above: editable.
 */
const SHELBY_ZIPS = new Set([
  "35007",
  "35040",
  "35043",
  "35051",
  "35078",
  "35080",
  "35089",
  "35114",
  "35115",
  "35123",
  "35124",
  "35143",
  "35144",
  "35147",
  "35176",
  "35178",
  "35186",
  "35187",
  "35242",
  "35244",
]);

/**
 * Which county a ZIP is in, as far as this application cares.
 *
 * `other` is a real answer, not a failure: a partner in Tuscaloosa is a partner,
 * they simply never get a Shelby or Jefferson storm text. Anything that is not
 * five digits is `other` too — a blank ZIP must not silently become a county.
 */
export function countyForZip(zip: string | null | undefined): PartnerCounty {
  const digits = (zip ?? "").trim().slice(0, 5);
  if (!/^[0-9]{5}$/.test(digits)) return "other";
  if (SHELBY_ZIPS.has(digits)) return "shelby";
  if (JEFFERSON_ZIPS.has(digits)) return "jefferson";
  return "other";
}

/* ----------------------------------------------------------------- names -- */

export interface PartnerName {
  first_name?: string | null;
  last_name?: string | null;
  company?: string | null;
}

/**
 * What to call them on a list row.
 *
 * A card with only a company on it is common — a two-person insurance office,
 * a trade with a truck. Falling back to the company is better than "—", and
 * better than refusing the save.
 */
export function partnerDisplayName(
  partner: PartnerName | null | undefined,
): string {
  if (!partner) return "—";
  const full = [partner.first_name, partner.last_name]
    .map((part) => part?.trim() ?? "")
    .filter(Boolean)
    .join(" ");
  return full || partner.company?.trim() || "—";
}

/**
 * The name to put at the front of a text.
 *
 * First name only, because that is how a text from somebody you met at a
 * lunch begins. When there is no first name the message has no natural
 * opening, so this returns the empty string and the caller decides — a
 * template rendered with a blank name is caught before anybody approves it.
 */
export function partnerFirstName(
  partner: PartnerName | null | undefined,
): string {
  return partner?.first_name?.trim() ?? "";
}

/* ---------------------------------------------------------------- phones -- */

/**
 * A US phone number in the form an `sms:` link needs.
 *
 * E.164 or null. There is no third option and no best-effort: a number that is
 * nearly right produces a link iOS refuses to open, and the rep sees nothing
 * happen and reports that the Send button is broken. Better to hold null,
 * which every screen can show as "no mobile number" and a person can fix.
 *
 * Extensions are dropped rather than guessed at — "205-555-0147 x12" is a desk
 * phone, and a desk phone is not the SMS target anyway.
 */
export function toE164(value: string | null | undefined): string | null {
  const raw = (value ?? "").trim();
  if (!raw) return null;

  // A number already written in international form is taken as given, as long
  // as it is plausible. A card from a partner with a Canadian mobile is not
  // this application's business to reformat.
  if (raw.startsWith("+")) {
    const digits = raw.slice(1).replace(/\D/g, "");
    return /^[1-9][0-9]{7,14}$/.test(digits) ? `+${digits}` : null;
  }

  const digits = raw.replace(/\D/g, "");
  if (digits.length === 10) return `+1${digits}`;
  if (digits.length === 11 && digits.startsWith("1")) return `+${digits}`;
  return null;
}

/** E.164 back to something a person reads: `+12055550147` → `205-555-0147`. */
export function formatE164(value: string | null | undefined): string | null {
  const raw = (value ?? "").trim();
  if (!raw) return null;
  const match = /^\+1([0-9]{3})([0-9]{3})([0-9]{4})$/.exec(raw);
  return match ? `${match[1]}-${match[2]}-${match[3]}` : raw;
}

/* ----------------------------------------------------------- the sms link -- */

/**
 * The link that opens Messages with the text already in it.
 *
 * Two quirks, both of which have cost somebody an afternoon before:
 *
 *  1. **iOS wants `&body=`, not `?body=`.** `sms:+12055550147?body=hi` opens
 *     Messages on iOS with an empty compose box and the whole query string
 *     silently discarded. The ampersand form is what Apple's own documentation
 *     shows and what actually works. Android accepts either; this app is built
 *     for the reps' iPhones, so the ampersand is what it emits.
 *  2. **It must be navigated to, not opened in a tab.** `window.open` on iOS
 *     gets popup-blocked from anything that is not a direct tap, and even when
 *     it is not, it leaves an orphan tab behind Messages that the rep comes
 *     back to instead of the send queue. `window.location.href = link` is the
 *     form that works — the caller's job, but it is the reason this returns a
 *     string rather than doing the navigation itself.
 *
 * Encoding is `encodeURIComponent`, which leaves `'` alone — apostrophes are
 * all over these messages and double-encoding them is how "I'll" becomes
 * "I%27ll" in somebody's inbox.
 */
export function smsLink(
  phoneE164: string | null | undefined,
  body: string,
): string | null {
  const to = toE164(phoneE164);
  if (!to) return null;
  const text = body.trim();
  if (!text) return `sms:${to}`;
  return `sms:${to}&body=${encodeURIComponent(text)}`;
}

/* --------------------------------------------------------- who may be texted */

export interface PartnerContactState {
  sms_status?: string | null;
  campaign_paused?: boolean | null;
  phone_mobile?: string | null;
}

/**
 * Why this partner cannot be texted, or null when they can be.
 *
 * Returns the sentence to put in the tooltip rather than a boolean, because
 * every caller that needs the boolean also needs to explain it, and a disabled
 * button with no reason on it is the thing people file bugs about.
 *
 * The order is the order a person would ask: is it allowed, then is it paused,
 * then is there a number to send to.
 */
export function whyNotTextable(
  partner: PartnerContactState | null | undefined,
): string | null {
  if (!partner) return "No partner.";
  if (partner.sms_status === "opted_out") {
    return "They asked not to be texted. Only they can undo that.";
  }
  if (partner.sms_status !== "opted_in") {
    return "Nobody has asked them yet. Get a yes in person, then tick the consent box on their page.";
  }
  if (partner.campaign_paused) {
    return "Paused. Unpause them on their page first.";
  }
  if (!toE164(partner.phone_mobile)) {
    return "No mobile number on file — an office line cannot receive a text.";
  }
  return null;
}

/** True when a batch may draft a message to this partner. */
export function isCampaignEligible(
  partner: PartnerContactState | null | undefined,
): boolean {
  return whyNotTextable(partner) === null;
}

/* ------------------------------------------------------------ the dedupe -- */

export interface PartnerDuplicateCandidate extends PartnerName {
  id: string;
  phone_mobile?: string | null;
  phone_office?: string | null;
  email?: string | null;
}

const normalizedEmail = (value: string | null | undefined): string =>
  (value ?? "").trim().toLowerCase();

/**
 * Partners already on file who look like the card just scanned.
 *
 * Soft on purpose. Two agents at the same brokerage share an office line, a
 * married couple share an email, and a hard block on either would refuse a
 * real partner at the exact moment the rep is standing in front of them. So
 * this warns and the rep decides — the same call ADR 0007 made for customers.
 *
 * Matching is on the mobile number and the email only. The office line is
 * excluded deliberately: it is the field most likely to be shared by people
 * who are genuinely different, and a warning that fires on every third card
 * from the same agency stops being read.
 */
export function findPartnerDuplicates(
  candidate: PartnerDuplicateCandidate,
  existing: readonly PartnerDuplicateCandidate[],
): PartnerDuplicateCandidate[] {
  const mobile = toE164(candidate.phone_mobile);
  const email = normalizedEmail(candidate.email);
  if (!mobile && !email) return [];

  return existing.filter((row) => {
    if (row.id === candidate.id) return false;
    if (mobile && toE164(row.phone_mobile) === mobile) return true;
    if (email && normalizedEmail(row.email) === email) return true;
    return false;
  });
}

/* ------------------------------------------------------- the send window -- */

/**
 * The hours a text from a person is a text from a person.
 *
 * Not enforced, and that is the decision rather than an omission. The rep is
 * the sender — it is their phone, their number, their relationship — and an
 * application that refused to build a link at 7pm would simply be worked
 * around. What it can usefully do is say what time it is where the partner is,
 * which is the thing somebody sending forty texts stops noticing.
 *
 * America/Chicago, always. A rep in a different time zone would otherwise be
 * warned about their own evening rather than the partner's.
 */
export const SEND_WINDOW = {
  startHour: 9,
  endHour: 18,
  endMinute: 30,
} as const;

interface CentralClock {
  hour: number;
  minute: number;
  weekday: number;
}

/**
 * The wall clock in Birmingham, from an instant.
 *
 * Derived through `Intl` rather than by subtracting six hours: Central time is
 * six hours behind UTC for part of the year and five for the rest, and a
 * hard-coded offset would put the warning an hour out for eight months.
 */
export function centralClock(at: Date): CentralClock {
  const parts = new Intl.DateTimeFormat("en-US", {
    timeZone: "America/Chicago",
    hour: "numeric",
    minute: "numeric",
    weekday: "short",
    hour12: false,
  }).formatToParts(at);

  const value = (type: string) =>
    parts.find((part) => part.type === type)?.value ?? "";

  const weekdays = ["Sun", "Mon", "Tue", "Wed", "Thu", "Fri", "Sat"];
  return {
    // `hour12: false` renders midnight as 24 in some engines and 0 in others.
    hour: Number(value("hour")) % 24,
    minute: Number(value("minute")),
    weekday: Math.max(0, weekdays.indexOf(value("weekday"))),
  };
}

/**
 * The nudge above the Send button, or null when the hour is unremarkable.
 *
 * Sunday gets its own line rather than being folded into "outside hours",
 * because the objection to a Sunday text is not the time of day.
 */
export function sendWindowWarning(at: Date): string | null {
  const { hour, minute, weekday } = centralClock(at);

  if (weekday === 0) {
    return "It's Sunday. These land better Monday morning.";
  }
  if (hour < SEND_WINDOW.startHour) {
    return "It's early. Before 9am reads as automated — worth waiting.";
  }
  if (
    hour > SEND_WINDOW.endHour ||
    (hour === SEND_WINDOW.endHour && minute > SEND_WINDOW.endMinute)
  ) {
    return "It's after 6:30pm. A text this late reads as a robot, not a friend.";
  }
  return null;
}

/**
 * How many texts an hour is sensible from one personal number.
 *
 * Carriers filter on volume from a single handset, and the number being
 * filtered here belongs to the rep personally — it is the number their family
 * calls. Thirty an hour is the figure the carriers' own guidance lands near,
 * and it is a hint on the screen rather than a limit in code for the same
 * reason the send window is.
 */
export const SUGGESTED_SENDS_PER_HOUR = 30;

/* ------------------------------------------------------------ the dates -- */

/**
 * A date-only value, read the way the person who typed it meant it.
 *
 * `met_on` and `event_date` are Postgres `date` columns and arrive as
 * `YYYY-MM-DD`. Handing that to `new Date()` parses it as **midnight UTC**,
 * which in Central time is seven o'clock the previous evening — so a card
 * scanned on the 27th displayed as the 26th, in the app, to the person who had
 * just scanned it.
 *
 * Parsing at noon UTC puts the instant safely inside the intended day in every
 * US time zone, which is the fix, and it is deliberately a shared function
 * rather than a habit: the bug is invisible for most of the day and only
 * appears to people who are not in UTC, which is everybody here.
 */
export function formatDateOnly(
  value: string | null | undefined,
): string | null {
  const raw = (value ?? "").trim();
  if (!/^\d{4}-\d{2}-\d{2}$/.test(raw)) return null;
  const at = new Date(`${raw}T12:00:00Z`);
  if (Number.isNaN(at.getTime())) return null;
  return at.toLocaleDateString("en-US", {
    timeZone: "America/Chicago",
    month: "short",
    day: "numeric",
    year: "numeric",
  });
}

/** Today in Birmingham, as `YYYY-MM-DD`, for a date input's default. */
export function todayCentral(at: Date = new Date()): string {
  const parts = new Intl.DateTimeFormat("en-CA", {
    timeZone: "America/Chicago",
    year: "numeric",
    month: "2-digit",
    day: "2-digit",
  }).format(at);
  return parts;
}

/* ------------------------------------------------------------ the switch -- */

/**
 * Whether the partner network is reachable at all.
 *
 * Jonathon, 2026-09-14: "hide referral partner process in roofboard. we will
 * not be using that." The feature was built and merged and never used —
 * production held no partners, no referrals and no messages on the day it was
 * turned off — so this hides it rather than deleting it (ADR 0144). Every
 * table and every screen stays; one row decides whether anybody can get to
 * them.
 *
 * Unknown reads as off, which is the opposite of how `showFinancing` treats a
 * setting that has not loaded, and deliberately so. Financing defends against
 * a block disappearing from under a rep mid-read; this defends against a tab
 * appearing for a moment and then being taken away, which is the worse of the
 * two on a section nobody is expecting to see.
 */
export function partnersEnabled(
  /** `company_settings.partners_enabled`. */
  setting: boolean | null | undefined,
): boolean {
  return setting === true;
}
